import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { type } from "arktype";
import { ArrowLeft, ArrowRight, Plus, RefreshCcw } from "lucide-react";
import { type ReactNode, useState } from "react";
import {
  type CategoryCode,
  capitalize,
  categoryDisplayNames,
  getFieldOptionDisplayName,
} from "shared";
import { toast } from "sonner";
import z from "zod";
import { GivefrontCardIcon } from "@/components/icons/credit-card-icon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAppForm } from "@/hooks/useCreateForm";
import type { RouterOutputs } from "@/lib/trpcClient";

const ruleTypes = ["card", "budget", "category"] as const;
type RuleType = (typeof ruleTypes)[number];

const validateSearch = type({
  "field?": "string",
  "type?": "'card' | 'budget' | 'category'",
});

export const Route = createFileRoute("/_authed/_org/accounting/settings/rules")(
  {
    component: RouteComponent,
    loader: ({ context: { queryClient, trpc } }) => {
      queryClient.ensureQueryData(trpc.budget.list.queryOptions());
      queryClient.ensureQueryData(trpc.card.list.queryOptions());
      queryClient.ensureQueryData(
        trpc.accounting.getAccountingFields.queryOptions(),
      );
      queryClient.ensureQueryData(trpc.accounting.listRules.queryOptions({}));
    },
    validateSearch,
  },
);

const createRuleValidator = z.object({
  accountingFieldOptionId: z.string().min(1, "Please select a field option"),
  forItem: z.string().min(1, "Please select an item"),
});

function getItemOptions(
  ruleType: RuleType,
  budgets: RouterOutputs["budget"]["list"],
  cards: RouterOutputs["card"]["list"],
): { display?: ReactNode; label: string; value: string }[] {
  if (ruleType === "category") {
    return Object.entries(categoryDisplayNames).map(([value, label]) => ({
      label,
      value,
    }));
  }
  if (ruleType === "budget") {
    return budgets.flatMap((budgetGroup) =>
      budgetGroup.subBudgets.map((budget) => ({
        display:
          budgetGroup.subBudgets.length === 1 ? (
            budget.name
          ) : (
            <div className="flex flex-col items-start text-sm">
              <span className="text-neutral-600 text-xs">
                {budgetGroup.name}
              </span>
              {budget.name}
            </div>
          ),
        label:
          budgetGroup.subBudgets.length === 1
            ? budget.name
            : `${budgetGroup.name} - ${budget.name}`,
        value: budget.id,
      })),
    );
  }
  if (ruleType === "card") {
    return cards.map((card) => ({
      display: (
        <div className="flex w-full grow items-center gap-2">
          <GivefrontCardIcon variant={card.type} className="h-7" />
          {card.name}

          <span className="ml-2 text-neutral-600 text-xs">
            (...{card.last4})
          </span>
        </div>
      ),
      label: card.name,
      value: card.id,
    }));
  }

  return [];
}

function RouteComponent() {
  const { trpc, queryClient } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const [selectedType, setSelectedType] = useState<RuleType | "">("");
  const [selectedFieldId, setSelectedFieldId] = useState("");

  const { data: fields } = useSuspenseQuery(
    trpc.accounting.getAccountingFields.queryOptions(),
  );
  const { data: budgets } = useSuspenseQuery(trpc.budget.list.queryOptions());
  const { data: cards } = useSuspenseQuery(trpc.card.list.queryOptions());
  const { data: allRules } = useSuspenseQuery(
    trpc.accounting.listRules.queryOptions({}),
  );

  const isDetailView = search.type && search.field;
  const ruleType = search.type as RuleType | undefined;
  const fieldId = search.field;
  const currentField = fields.find((f) => f.id === fieldId);

  const filteredRules =
    isDetailView && ruleType && fieldId
      ? allRules.filter(
          (r) => r.type === ruleType && r.accountingFieldId === fieldId,
        )
      : [];

  const { mutate: createRule, isPending: isCreating } = useMutation(
    trpc.accounting.createAccountingRule.mutationOptions({
      onError: (error) => {
        toast.error("Failed to create rule", {
          description: error.message,
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries(
          trpc.accounting.listRules.queryOptions({}),
        );
        toast.success("Rule created successfully");
        form.reset();
      },
    }),
  );

  const form = useAppForm({
    defaultValues: {
      accountingFieldOptionId: "",
      forItem: "",
    } as z.infer<typeof createRuleValidator>,
    onSubmit: ({ value }) => {
      if (!ruleType || !fieldId) return;
      createRule({
        accountingFieldId: fieldId,
        accountingFieldOptionId: value.accountingFieldOptionId,
        forItem: value.forItem,
        type: ruleType,
      });
    },
    validators: {
      onSubmit: createRuleValidator,
    },
  });

  const enabledOptions =
    currentField?.options?.filter((option) => option.status === "enabled") ||
    [];

  const itemOptions = ruleType ? getItemOptions(ruleType, budgets, cards) : [];
  const existingForItems = new Set(filteredRules.map((r) => r.forItem));
  const availableItemOptions = itemOptions.filter(
    (option) => !existingForItems.has(option.value),
  );

  const getItemDisplayName = (forItem: string, type: RuleType) => {
    if (type === "category") {
      return categoryDisplayNames[forItem as CategoryCode] || forItem;
    }
    if (type === "budget") {
      for (const budgetGroup of budgets) {
        const budget = budgetGroup.subBudgets.find((b) => b.id === forItem);
        if (budget)
          return budgetGroup.subBudgets.length === 1
            ? budget.name
            : `${budgetGroup.name} - ${budget.name}`;
      }
      return forItem;
    }
    if (type === "card") {
      const card = cards.find((c) => c.id === forItem);
      return card?.name || forItem;
    }
    return forItem;
  };

  const ruleGroups = allRules.reduce(
    (acc, rule) => {
      const key = `${rule.type}-${rule.accountingFieldId}`;
      if (!acc[key]) {
        acc[key] = {
          count: 0,
          fieldId: rule.accountingFieldId,
          fieldName:
            rule.accountingField.displayName || rule.accountingField.name,
          type: rule.type as RuleType,
        };
      }
      acc[key].count++;
      return acc;
    },
    {} as Record<
      string,
      { type: RuleType; fieldId: string; fieldName: string; count: number }
    >,
  );

  const groupedRules = Object.values(ruleGroups);

  if (isDetailView && ruleType && fieldId) {
    if (!currentField) {
      return <div>Field not found</div>;
    }

    return (
      <div className="flex h-full flex-col">
        <div className="border-b pt-6 pb-2">
          <Button variant="text" className="m-0 mx-6 h-4 w-16 p-0" asChild>
            <Link
              to="/accounting/settings/rules"
              search={{}}
              onClick={(e) => {
                e.preventDefault();
                navigate({ search: {} });
              }}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              <span>Back</span>
            </Link>
          </Button>
          <SheetHeader>
            <SheetTitle>
              {capitalize(ruleType)} →{" "}
              {currentField.displayName || currentField.name}
            </SheetTitle>
            <SheetDescription>
              Create rules to automatically assign{" "}
              {currentField.displayName || currentField.name} based on{" "}
              {ruleType === "category" ? "spend category" : ruleType}
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="flex min-h-0 grow flex-col overflow-y-auto">
          <div className="p-6">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-base">Add New Rule</CardTitle>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    form.handleSubmit();
                  }}
                >
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-row items-end gap-4">
                      <div className="flex flex-1 flex-col space-y-2">
                        <form.AppField
                          name="forItem"
                          children={(field) => (
                            <field.SelectSearchField
                              label={`When ${ruleType} is`}
                              options={availableItemOptions}
                              required
                              placeholder={`Select ${ruleType}...`}
                            />
                          )}
                        />
                      </div>

                      <span className="mb-2 text-muted-foreground">→</span>

                      <div className="flex flex-1 flex-col space-y-2">
                        <form.AppField
                          name="accountingFieldOptionId"
                          children={(field) => (
                            <field.SelectSearchField
                              label={`Set ${currentField.displayName || currentField.name} to`}
                              options={enabledOptions.map((option) => ({
                                label: getFieldOptionDisplayName(option),
                                value: option.id,
                              }))}
                              required
                              placeholder="Select option..."
                            />
                          )}
                        />
                      </div>

                      <Button type="submit" disabled={isCreating}>
                        {isCreating ? (
                          <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Plus className="mr-2 h-4 w-4" />
                        )}
                        Add Rule
                      </Button>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>

            {filteredRules.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 text-xs [&>th]:font-medium! [&>th]:text-gray-500">
                    <TableHead className="w-[40%] px-2">
                      {capitalize(ruleType)}
                    </TableHead>
                    <TableHead className="w-[40%] px-2">
                      {currentField.displayName || currentField.name}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell className="font-medium">
                        {getItemDisplayName(rule.forItem, ruleType)}
                      </TableCell>
                      <TableCell>
                        {rule.accountingFieldOption
                          ? getFieldOptionDisplayName(
                              rule.accountingFieldOption,
                            )
                          : "Unknown"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="text-muted-foreground text-sm">
                  No rules configured yet. Add your first rule above.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b pt-6 pb-2">
        <Button variant="text" className="m-0 mx-6 h-4 w-16 p-0" asChild>
          <Link to="/accounting/settings">
            <ArrowLeft className="mr-2 h-4 w-4" />
            <span>Back</span>
          </Link>
        </Button>
        <SheetHeader>
          <SheetTitle>Coding Rules</SheetTitle>
          <SheetDescription>
            Automatically assign accounting fields based on a transaction's
            card, budget, or spend category
          </SheetDescription>
        </SheetHeader>
      </div>

      <div className="flex flex-col space-y-6 p-6">
        <div className="flex flex-col space-y-4">
          <p className="text-muted-foreground text-sm">
            Select a Givefront field and an accounting field to view and manage
            coding rules for that combination.
          </p>

          <div className="flex flex-row items-end gap-4">
            <div className="flex flex-1 flex-col space-y-2">
              <Label htmlFor="givefront-field">Givefront Field</Label>
              <Select
                value={selectedType}
                onValueChange={(value: RuleType) => setSelectedType(value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {ruleTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {capitalize(type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <span className="mb-2 text-muted-foreground">→</span>

            <div className="flex flex-1 flex-col space-y-2">
              <Label htmlFor="accounting-field">Accounting Field</Label>
              <Select
                value={selectedFieldId}
                onValueChange={(value: string) => setSelectedFieldId(value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select field..." />
                </SelectTrigger>
                <SelectContent>
                  {fields.map((field) => (
                    <SelectItem key={field.id} value={field.id}>
                      {field.displayName || field.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              disabled={!selectedType || !selectedFieldId}
              onClick={() => {
                if (selectedType && selectedFieldId) {
                  navigate({
                    search: { field: selectedFieldId, type: selectedType },
                  });
                }
              }}
            >
              Create Rule
            </Button>
          </div>
        </div>

        {groupedRules.length > 0 && (
          <>
            <div className="h-px w-full bg-border" />

            <div className="flex flex-col space-y-4">
              <h3 className="font-medium text-sm">Existing Rules</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {groupedRules.map((group) => (
                  <Link
                    key={`${group.type}-${group.fieldId}`}
                    to="/accounting/settings/rules"
                    search={{ field: group.fieldId, type: group.type }}
                  >
                    <Card className="cursor-pointer transition-all duration-200 hover:border-primary/20 hover:bg-muted/30">
                      <CardHeader className="py-3">
                        <CardTitle className="flex items-center justify-between text-sm">
                          <span>
                            {capitalize(group.type)} → {group.fieldName}
                          </span>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{group.count}</Badge>
                            <ArrowRight className="h-4 w-4 text-muted-foreground" />
                          </div>
                        </CardTitle>
                      </CardHeader>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
